import React, { useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, Building2, Wrench, Calendar, CheckCircle2, Clock, Loader2,
  Trash2, XCircle, Repeat, Phone, Camera, ImagePlus, X,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

const WO_STATUS_STYLE = {
  open: 'bg-amber-100 text-amber-700',
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-slate-100 text-slate-500',
};
const PRIORITY_STYLE = {
  emergency: 'bg-rose-100 text-rose-700 border-rose-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  normal: 'bg-slate-100 text-slate-600 border-slate-200',
  low: 'bg-slate-50 text-slate-500 border-slate-200',
};
const FREQUENCIES = ['weekly', 'biweekly', 'monthly', 'quarterly'];

const StatCard = ({ icon: Icon, label, value, tone }) => (
  <div className="rounded-2xl border border-[#E6E9EE] bg-white p-4 flex items-center gap-3">
    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone || 'bg-[#F2F7F5]'}`}>
      <Icon className="h-5 w-5 text-[#17352D]" />
    </div>
    <div>
      <div className="text-2xl font-bold text-[#17352D] leading-none">{value}</div>
      <div className="text-[11px] text-slate-500 mt-1">{label}</div>
    </div>
  </div>
);

export default function CommercialPortalPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newProp, setNewProp] = useState({ name: '', address: '', city: '', type: 'office' });
  const [newWO, setNewWO] = useState({ property_id: '', service_name: '', priority: 'normal', description: '', photos: [] });
  const [newPlan, setNewPlan] = useState({ property_id: '', service_name: '', frequency: 'monthly', description: '' });
  const [openProp, setOpenProp] = useState(false);
  const [openWO, setOpenWO] = useState(false);
  const [openPlan, setOpenPlan] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const MAX_PHOTOS = 8;

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    const room = MAX_PHOTOS - (newWO.photos?.length || 0);
    if (room <= 0) { toast.error(`Maximum ${MAX_PHOTOS} photos per request`); return; }
    setUploadingPhotos(true);
    try {
      const urls = [];
      for (const f of files.slice(0, room)) {
        const fd = new FormData();
        fd.append('file', f);
        const r = await api.post('/uploads', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.url) urls.push(r.data.url);
      }
      setNewWO(prev => ({ ...prev, photos: [...(prev.photos || []), ...urls].slice(0, MAX_PHOTOS) }));
      if (urls.length) toast.success(`${urls.length} photo${urls.length > 1 ? 's' : ''} attached`);
      if (files.length > room) toast.warning(`Only ${room} more photo${room > 1 ? 's' : ''} allowed — extras skipped`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed — try again');
    } finally {
      setUploadingPhotos(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const removePhoto = (url) => setNewWO(prev => ({ ...prev, photos: (prev.photos || []).filter(p => p !== url) }));

  const refresh = async () => {
    try {
      const [p, w] = await Promise.all([api.get('/commercial/properties'), api.get('/commercial/work-orders')]);
      setProperties(p.data.properties); setWorkOrders(w.data.work_orders);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const requests = workOrders.filter(w => !w.recurring);
  const plans = workOrders.filter(w => w.recurring);
  const openCount = requests.filter(w => w.status === 'open').length;
  const inProgressCount = requests.filter(w => ['scheduled', 'in_progress'].includes(w.status)).length;
  const doneCount = requests.filter(w => w.status === 'completed').length;

  const createProp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/commercial/properties', { ...newProp, locations: [] });
      toast.success('Property added');
      setOpenProp(false); setNewProp({ name: '', address: '', city: '', type: 'office' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add property');
    } finally { setBusy(false); }
  };

  const createWO = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/commercial/work-orders', newWO);
      toast.success('Work order submitted — our team has been notified');
      setOpenWO(false); setNewWO({ property_id: '', service_name: '', priority: 'normal', description: '', photos: [] });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit');
    } finally { setBusy(false); }
  };

  const createPlan = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/commercial/work-orders', { ...newPlan, priority: 'normal', recurring: true });
      toast.success('Maintenance plan requested — we\'ll confirm your schedule shortly');
      setOpenPlan(false); setNewPlan({ property_id: '', service_name: '', frequency: 'monthly', description: '' });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit');
    } finally { setBusy(false); }
  };

  const cancelWO = async (id) => {
    try {
      await api.patch(`/commercial/work-orders/${id}`, { status: 'cancelled' });
      toast.success('Request cancelled');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel');
    }
  };

  const deleteProp = async (id) => {
    try {
      await api.delete(`/commercial/properties/${id}`);
      toast.success('Property removed');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove property');
    }
  };

  const propName = (id) => properties.find(p => p.id === id)?.name || 'Property';

  const WOCard = ({ w }) => (
    <div className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid={`commercial-wo-${w.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {w.recurring
            ? <Repeat className="h-5 w-5 text-[#C8922F] mt-1 shrink-0" />
            : <Wrench className="h-5 w-5 text-[#C8922F] mt-1 shrink-0" />}
          <div>
            <div className="font-headline text-lg text-[#17352D]">{w.service_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {propName(w.property_id)}
              {w.recurring && w.frequency ? ` · ${w.frequency}` : ''}
              {' · '}{String(w.created_at || '').slice(0, 10)}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={`text-[10px] font-bold px-2 py-1 rounded-full capitalize ${WO_STATUS_STYLE[w.status] || WO_STATUS_STYLE.open}`} data-testid={`wo-status-${w.id}`}>
            {(w.status || 'open').replace('_', ' ')}
          </span>
          {!w.recurring && w.priority !== 'normal' && (
            <Badge variant="outline" className={`rounded-full text-[10px] capitalize ${PRIORITY_STYLE[w.priority] || ''}`}>{w.priority}</Badge>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-700 mt-3">{w.description}</p>
      {(w.photos?.length || 0) > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`wo-photos-${w.id}`}>
          {w.photos.map((url, i) => (
            <a key={url + i} href={url} target="_blank" rel="noreferrer" className="block h-16 w-16 rounded-lg overflow-hidden border border-[#E6E9EE] hover:ring-2 hover:ring-[#C8922F]/60 transition-shadow" title="View full size">
              <img src={url} alt={`Request photo ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}
      {w.scheduled_date && (
        <div className="mt-2 text-xs font-semibold text-blue-700 flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" /> Scheduled: {w.scheduled_date}
        </div>
      )}
      {w.admin_notes && (
        <div className="mt-2 rounded-xl bg-[#F7F9F8] border border-[#EEF1F0] p-3 text-xs text-slate-600">
          <span className="font-semibold text-[#17352D]">Update from Royal Palm:</span> {w.admin_notes}
        </div>
      )}
      {w.status === 'open' && (
        <button
          onClick={() => cancelWO(w.id)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-rose-600 transition-colors"
          data-testid={`wo-cancel-${w.id}`}
        >
          <XCircle className="h-3.5 w-3.5" /> Cancel request
        </button>
      )}
    </div>
  );

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.18em] text-[#C8922F] font-semibold">Client Portal</div>
          <h1 className="mt-1 font-headline text-3xl md:text-4xl text-[#17352D]" data-testid="portal-welcome">
            Welcome back{user?.company || user?.name ? `, ${user.company || user.name}` : ''}
          </h1>
          <p className="text-sm text-slate-500 mt-1">Manage your properties, service requests and maintenance plans in one place.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="portal-stats">
            <StatCard icon={Building2} label="Properties" value={properties.length} />
            <StatCard icon={Clock} label="Open requests" value={openCount} tone="bg-amber-50" />
            <StatCard icon={Loader2} label="Scheduled / in progress" value={inProgressCount} tone="bg-blue-50" />
            <StatCard icon={CheckCircle2} label="Completed" value={doneCount} tone="bg-emerald-50" />
          </div>
        )}

        <Tabs defaultValue="work-orders" className="w-full">
          <TabsList className="grid grid-cols-3 w-full bg-[#F2F7F5] rounded-2xl p-1">
            <TabsTrigger value="work-orders" data-testid="commercial-tab-work-orders" className="rounded-xl">Service Requests ({requests.length})</TabsTrigger>
            <TabsTrigger value="properties" data-testid="commercial-tab-properties" className="rounded-xl">Properties ({properties.length})</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="commercial-tab-maintenance" className="rounded-xl">Maintenance Plans ({plans.length})</TabsTrigger>
          </TabsList>

          {/* ---- SERVICE REQUESTS ---- */}
          <TabsContent value="work-orders" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-slate-600 text-sm">Submit and track service requests for your properties</div>
              <Dialog open={openWO} onOpenChange={setOpenWO}>
                <DialogTrigger asChild>
                  <Button data-testid="commercial-add-wo-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white" disabled={properties.length === 0}><Plus className="h-4 w-4 mr-1" /> New request</Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader><DialogTitle>Submit service request</DialogTitle>
                    <DialogDescription>Our team is notified instantly and will confirm scheduling.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={createWO} className="space-y-3" data-testid="commercial-wo-form">
                    <div><Label>Property</Label>
                      <Select value={newWO.property_id} onValueChange={(v) => setNewWO({ ...newWO, property_id: v })}>
                        <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="commercial-wo-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Service needed</Label><Input required value={newWO.service_name} onChange={e => setNewWO({ ...newWO, service_name: e.target.value })} className="h-11 rounded-xl mt-1" placeholder="e.g. Pressure washing" data-testid="commercial-wo-service" /></div>
                    <div><Label>Priority</Label>
                      <Select value={newWO.priority} onValueChange={(v) => setNewWO({ ...newWO, priority: v })}>
                        <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="commercial-wo-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>{['low', 'normal', 'high', 'emergency'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Describe the issue</Label><Textarea required rows={4} value={newWO.description} onChange={e => setNewWO({ ...newWO, description: e.target.value })} className="rounded-xl mt-1" data-testid="commercial-wo-desc" /></div>

                    {/* ---- Photos ---- */}
                    <div>
                      <Label>Photos <span className="text-slate-400 font-normal">(optional, up to 8)</span></Label>
                      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => addPhotos(e.target.files)} data-testid="commercial-wo-camera-input" />
                      <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={e => addPhotos(e.target.files)} data-testid="commercial-wo-gallery-input" />
                      <div className="flex gap-2 mt-1.5">
                        <Button type="button" variant="outline" size="sm" onClick={() => cameraInputRef.current?.click()} disabled={uploadingPhotos || (newWO.photos?.length || 0) >= MAX_PHOTOS} className="rounded-xl h-10 flex-1" data-testid="commercial-wo-take-photo">
                          <Camera className="h-4 w-4 mr-1.5" /> Take photo
                        </Button>
                        <Button type="button" variant="outline" size="sm" onClick={() => galleryInputRef.current?.click()} disabled={uploadingPhotos || (newWO.photos?.length || 0) >= MAX_PHOTOS} className="rounded-xl h-10 flex-1" data-testid="commercial-wo-add-photos">
                          {uploadingPhotos ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ImagePlus className="h-4 w-4 mr-1.5" />} Add photos
                        </Button>
                      </div>
                      {(newWO.photos?.length || 0) > 0 && (
                        <div className="grid grid-cols-4 gap-2 mt-2" data-testid="commercial-wo-photo-previews">
                          {newWO.photos.map((url) => (
                            <div key={url} className="relative group rounded-lg overflow-hidden border border-[#E6E9EE] aspect-square">
                              <img src={url} alt="Attached" className="h-full w-full object-cover" />
                              <button
                                type="button"
                                onClick={() => removePhoto(url)}
                                className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 hover:bg-rose-600 text-white flex items-center justify-center transition-colors"
                                data-testid={`commercial-wo-remove-photo-${newWO.photos.indexOf(url)}`}
                                title="Remove photo"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <DialogFooter><Button type="submit" disabled={busy || uploadingPhotos || !newWO.property_id} data-testid="commercial-wo-submit" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit request'}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="commercial-work-orders-list">
              {requests.map(w => <WOCard key={w.id} w={w} />)}
              {requests.length === 0 && (
                <div className="col-span-full rounded-2xl border border-dashed border-[#E6E9EE] p-10 text-center">
                  <Wrench className="h-8 w-8 text-slate-300 mx-auto" />
                  <div className="text-slate-500 mt-2 text-sm">No service requests yet. Submit your first one and our team will jump on it.</div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ---- PROPERTIES ---- */}
          <TabsContent value="properties" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-slate-600 text-sm">Properties you manage with Royal Palm</div>
              <Dialog open={openProp} onOpenChange={setOpenProp}>
                <DialogTrigger asChild>
                  <Button data-testid="commercial-add-property-button" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white"><Plus className="h-4 w-4 mr-1" /> Add property</Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Add property</DialogTitle></DialogHeader>
                  <form onSubmit={createProp} className="space-y-3" data-testid="commercial-property-form">
                    <div><Label>Name</Label><Input required value={newProp.name} onChange={e => setNewProp({ ...newProp, name: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="commercial-prop-name" /></div>
                    <div><Label>Address</Label><Input required value={newProp.address} onChange={e => setNewProp({ ...newProp, address: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="commercial-prop-address" /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label>City</Label><Input value={newProp.city} onChange={e => setNewProp({ ...newProp, city: e.target.value })} className="h-11 rounded-xl mt-1" data-testid="commercial-prop-city" /></div>
                      <div><Label>Type</Label>
                        <Select value={newProp.type} onValueChange={(v) => setNewProp({ ...newProp, type: v })}>
                          <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="commercial-prop-type"><SelectValue /></SelectTrigger>
                          <SelectContent>{['office', 'retail', 'warehouse', 'medical', 'hoa', 'apartment', 'restaurant', 'other'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter><Button type="submit" disabled={busy} data-testid="commercial-prop-submit" className="rounded-xl bg-[#17352D] hover:bg-[#1E4338] text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="commercial-properties-list">
              {properties.map(p => {
                const propOpen = workOrders.filter(w => w.property_id === p.id && ['open', 'scheduled', 'in_progress'].includes(w.status)).length;
                return (
                  <div key={p.id} className="rounded-2xl border border-[#E6E9EE] bg-white p-5" data-testid={`commercial-property-${p.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <Building2 className="h-5 w-5 text-[#C8922F] mt-1" />
                        <div>
                          <div className="font-headline text-xl text-[#17352D]">{p.name}</div>
                          <div className="text-sm text-slate-600">{p.address}{p.city ? `, ${p.city}` : ''}</div>
                          <div className="flex items-center gap-2 mt-1.5">
                            <Badge variant="outline" className="rounded-full text-[10px] capitalize">{p.type}</Badge>
                            {propOpen > 0 && <Badge variant="outline" className="rounded-full text-[10px] bg-amber-50 text-amber-700 border-amber-200">{propOpen} active request{propOpen > 1 ? 's' : ''}</Badge>}
                          </div>
                        </div>
                      </div>
                      <button onClick={() => deleteProp(p.id)} className="text-slate-300 hover:text-rose-500 transition-colors" data-testid={`commercial-prop-delete-${p.id}`} title="Remove property">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {properties.length === 0 && <div className="col-span-full text-slate-500">No properties yet. Add your first property.</div>}
            </div>
          </TabsContent>

          {/* ---- MAINTENANCE PLANS ---- */}
          <TabsContent value="maintenance" className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-slate-600 text-sm">Recurring maintenance tailored to each property</div>
              <Dialog open={openPlan} onOpenChange={setOpenPlan}>
                <DialogTrigger asChild>
                  <Button data-testid="commercial-add-plan-button" className="rounded-xl bg-[#C8922F] hover:bg-[#B27F26] text-white" disabled={properties.length === 0}><Repeat className="h-4 w-4 mr-1" /> Request a plan</Button>
                </DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader><DialogTitle>Request maintenance plan</DialogTitle>
                    <DialogDescription>Tell us what you need and how often — we'll confirm pricing and schedule.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={createPlan} className="space-y-3" data-testid="commercial-plan-form">
                    <div><Label>Property</Label>
                      <Select value={newPlan.property_id} onValueChange={(v) => setNewPlan({ ...newPlan, property_id: v })}>
                        <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="commercial-plan-property"><SelectValue placeholder="Select property" /></SelectTrigger>
                        <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Service</Label><Input required value={newPlan.service_name} onChange={e => setNewPlan({ ...newPlan, service_name: e.target.value })} className="h-11 rounded-xl mt-1" placeholder="e.g. Landscape maintenance" data-testid="commercial-plan-service" /></div>
                    <div><Label>Frequency</Label>
                      <Select value={newPlan.frequency} onValueChange={(v) => setNewPlan({ ...newPlan, frequency: v })}>
                        <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="commercial-plan-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent>{FREQUENCIES.map(f => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Details</Label><Textarea required rows={3} value={newPlan.description} onChange={e => setNewPlan({ ...newPlan, description: e.target.value })} className="rounded-xl mt-1" placeholder="Scope, areas to cover, preferred days…" data-testid="commercial-plan-desc" /></div>
                    <DialogFooter><Button type="submit" disabled={busy || !newPlan.property_id} data-testid="commercial-plan-submit" className="rounded-xl bg-[#C8922F] hover:bg-[#B27F26] text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Request plan'}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="commercial-plans-list">
              {plans.map(w => <WOCard key={w.id} w={w} />)}
              {plans.length === 0 && (
                <div className="col-span-full rounded-2xl border border-[#EED7A6] bg-[#FBF7EE] p-6">
                  <div className="flex items-center gap-3"><Calendar className="h-5 w-5 text-[#C8922F]" /><div className="font-headline text-xl text-[#17352D]">No plans yet</div></div>
                  <p className="text-slate-700 mt-2 text-sm">Set up recurring maintenance — monthly cleaning, quarterly landscape refresh, semi-annual HVAC service and more. Request a plan above, or call <a href="tel:5618848900" className="underline font-semibold inline-flex items-center gap-0.5"><Phone className="h-3 w-3" />561-884-8900</a>.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </Layout>
  );
}
